/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import {
  ActiveElement,
  ChartData,
  ChartEvent,
  ChartOptions,
  ScatterDataPoint,
  Tick,
  TooltipItem,
  TooltipModel,
} from "chart.js"
import { useMemo, useRef } from "react"
import { Bar } from "react-chartjs-2"
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types"
import { Box } from "../../Box"
import { BarChartDataset } from "../types"
import { fixedOptions } from "./options"
import * as styles from "./styles/barChartStyles"

export type BarChartRef<TData, TLabel> = ChartJSOrUndefined<"bar", TData, TLabel>

export type BarChartProps<TData, TLabel> = {
  animation?: boolean
  annotations?: object
  clickHandler?: (
    charRef: ChartJSOrUndefined<"bar", TData, TLabel>,
    e: React.MouseEvent<HTMLCanvasElement, MouseEvent>
  ) => void
  datasets: BarChartDataset<TData>
  height?: number
  labels: TLabel[]
  maintainAspectRatio?: boolean
  title?: string
  toolTipEnabled?: boolean
  toolTipLabelFn?: (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) => string[] | string
  toolTipTitleFn?: (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">[]) => string[] | string
  toolTipBeforeBodyFn?: (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">[]) => string[] | string
  toolTipLabelColorFn?: (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) => string
  width?: number
  xAxisTickColor?: Array<string> | string
  yAxisMaxValue?: number
  yAxisStepSize?: number
  yTickFn?: (tickValue: number, index: number, ticks: Tick[]) => string | number | undefined
  displayXAxisGrid?: boolean
  displayYAxisGrid?: boolean
  onChartClick?: (event: ChartEvent, chartElement: ActiveElement[]) => void
}

export const BarChartComponent = <TData = (number | ScatterDataPoint | null)[], TLabel = unknown>({
  animation,
  annotations,
  clickHandler,
  datasets,
  height = 200,
  labels,
  maintainAspectRatio,
  title,
  toolTipEnabled,
  toolTipLabelFn,
  toolTipTitleFn,
  toolTipBeforeBodyFn,
  toolTipLabelColorFn,
  width = 500,
  xAxisTickColor,
  yAxisMaxValue,
  yAxisStepSize,
  yTickFn,
  displayXAxisGrid = false,
  displayYAxisGrid = false,
  onChartClick,
}: BarChartProps<TData, TLabel>) => {
  const chartRef = useRef<ChartJSOrUndefined<"bar", TData, TLabel>>()

  const chartData = useMemo<ChartData<"bar", TData, TLabel>>(
    () => ({
      labels,
      datasets: (datasets || []).map((x) => {
        const { datasetOptions } = x
        return {
          data: x.data,
          ...datasetOptions,
        }
      }),
    }),
    [labels, datasets]
  )

  // @ts-ignore
  const options = useMemo<ChartOptions<"bar">>(() => {
    const yTickFixedFn = (tickValue: number): string => `${tickValue}`

    return {
      ...fixedOptions,
      onHover: (event, chartElement) => {
        const target = event?.native?.target as EventTarget & {
          style: {
            cursor: string
          }
        }
        target && (target.style.cursor = chartElement[0] ? `pointer` : `default`)
      },
      onClick: onChartClick,
      maintainAspectRatio,
      animation,
      scales: {
        ...fixedOptions.scales,
        x: {
          ...fixedOptions.scales?.x,
          ticks: {
            ...fixedOptions.scales?.x?.ticks,
            color: xAxisTickColor,
          },
          stacked: true,
          grid: {
            display: displayXAxisGrid,
          },
        },
        y: {
          max: yAxisMaxValue,
          ...fixedOptions.scales?.y,
          ticks: {
            ...fixedOptions.scales?.y?.ticks,
            stepSize: yAxisStepSize,
            display: true,
            callback: yTickFn || yTickFixedFn,
          },
          grid: {
            display: displayYAxisGrid,
          },
          title: {
            display: title !== undefined,
            text: title,
            font: {
              size: 12,
            },
          },
          stacked: true,
        },
      },
      plugins: {
        ...fixedOptions.plugins,
        tooltip: {
          ...fixedOptions.plugins?.tooltip,
          enabled: toolTipEnabled,
          callbacks: {
            beforeBody: toolTipBeforeBodyFn,
            label: toolTipLabelFn,
            labelTextColor: toolTipLabelColorFn,
            title: toolTipTitleFn,
          },
        },
        annotation: annotations,
      },
    }
  }, [
    maintainAspectRatio,
    animation,
    xAxisTickColor,
    displayXAxisGrid,
    yAxisMaxValue,
    yAxisStepSize,
    yTickFn,
    displayYAxisGrid,
    title,
    toolTipEnabled,
    toolTipBeforeBodyFn,
    toolTipLabelFn,
    toolTipLabelColorFn,
    toolTipTitleFn,
    annotations,
    onChartClick,
  ])

  return (
    <Box css={styles.root}>
      <Bar<TData, TLabel>
        ref={chartRef}
        onClick={(e) => clickHandler && clickHandler(chartRef.current, e)}
        options={options}
        data={chartData}
        height={height}
        width={width}
      />
    </Box>
  )
}

BarChartComponent.defaultProps = {
  animation: true,
  maintainAspectRatio: true,
  clickHandler: () => {},
  height: 200,
  toolTipEnabled: true,
  toolTipTitleFn: undefined,
  toolTipBeforeBodyFn: undefined,
  toolTipLabelColorFn: undefined,
  width: 500,
  xAxisTickColor: undefined,
  yAxisMaxValue: undefined,
  yAxisStepSize: undefined,
  yTickFn: undefined,
  title: undefined,
}
